/**
 * Report the taps that answer late, with what was tapped (#865).
 *
 * `$web_vitals` says a session's worst interaction took 2 s and nothing more.
 * This watcher observes the browser's own Event Timing entries and reports
 * every interaction over `SLOW_INTERACTION_MS` as `slow_interaction`: the
 * element (test id, aria-label or button text), the route, and the split
 * between input delay (the main thread was busy before the tap), processing
 * (the handlers) and presentation (the paint after). Where the browser has the
 * Long Tasks API (Chrome), a task over `LONG_TASK_MS` is `long_task` with the
 * last tap before it. Both are budgeted per page, so a stuck page cannot
 * flood a session. Web only; never throws into the app.
 */
import {
  describeTarget,
  makeBudget,
  summarizeInteractions,
  tapBefore,
  type InteractionEntryLike,
  type TapRecord,
} from '../utils/interactionReport';

export const SLOW_INTERACTION_MS = 200;
export const LONG_TASK_MS = 250;
const REPORTS_PER_PAGE = 30; // per kind
const TAP_MEMORY = 20;
const TAP_LOOKBACK_MS = 10_000;
const FLUSH_QUIET_MS = 1000;

type Track = (event: string, properties?: Record<string, unknown>) => void;

let started = false;

function route(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '';
}

export function startMainThreadWatch(track: Track): void {
  if (started) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (typeof PerformanceObserver === 'undefined') return;
  started = true;

  const taps: TapRecord[] = [];
  const remember = (e: Event) => {
    const { label, testId } = describeTarget(e.target as any);
    taps.push({ t: performance.now(), label, testId });
    if (taps.length > TAP_MEMORY) taps.shift();
  };
  for (const type of ['pointerdown', 'keydown']) {
    document.addEventListener(type, remember, { capture: true, passive: true });
  }

  // One budget per kind: a dev bundle's boot alone is dozens of long tasks,
  // and they must not spend the slow interactions' budget.
  const allowInteraction = makeBudget(REPORTS_PER_PAGE);
  const allowTask = makeBudget(REPORTS_PER_PAGE);
  const supported: string[] = (PerformanceObserver as any).supportedEntryTypes ?? [];

  // Slow interactions: entries of one tap arrive one by one; they are grouped
  // and reported after a quiet second, the way INP groups them.
  if (supported.includes('event')) {
    let pending: InteractionEntryLike[] = [];
    let timer: ReturnType<typeof setTimeout> | undefined;
    const flush = () => {
      timer = undefined;
      const batch = pending;
      pending = [];
      for (const i of summarizeInteractions(batch)) {
        if (i.durationMs < SLOW_INTERACTION_MS || !allowInteraction()) continue;
        const target = i.target ? describeTarget(i.target as any) : tapBefore(taps, i.startTime, 2000);
        track('slow_interaction', {
          duration_ms: Math.round(i.durationMs),
          input_delay_ms: Math.round(i.inputDelayMs),
          processing_ms: Math.round(i.processingMs),
          presentation_ms: Math.round(i.presentationMs),
          event_type: i.eventType,
          target: target?.label ?? 'unknown',
          target_testid: target?.testId,
          route: route(),
        });
      }
    };
    try {
      const po = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as any[]) {
          if (!entry.interactionId) continue;
          pending.push(entry as InteractionEntryLike);
        }
        if (pending.length && !timer) timer = setTimeout(flush, FLUSH_QUIET_MS);
      });
      po.observe({ type: 'event', durationThreshold: SLOW_INTERACTION_MS, buffered: true } as any);
    } catch (err) {
      console.warn('[mainThreadWatch] event timing unavailable', err);
    }
  }

  // Long tasks (Chrome): the task itself, with the last tap before it.
  if (supported.includes('longtask')) {
    try {
      const po = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as any[]) {
          if (entry.duration < LONG_TASK_MS || !allowTask()) continue;
          const tap = tapBefore(taps, entry.startTime, TAP_LOOKBACK_MS);
          const container = entry.attribution?.[0];
          track('long_task', {
            duration_ms: Math.round(entry.duration),
            route: route(),
            last_tap: tap?.label,
            last_tap_testid: tap?.testId,
            since_tap_ms: tap ? Math.round(entry.startTime - tap.t) : undefined,
            container_type: container?.containerType,
            container_name: container?.containerName || container?.containerId || undefined,
          });
        }
      });
      po.observe({ type: 'longtask', buffered: false });
    } catch (err) {
      console.warn('[mainThreadWatch] long tasks unavailable', err);
    }
  }
}

/** Tests only. */
export function _resetMainThreadWatch(): void {
  started = false;
}
